import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useModalStore } from '@/lib/stores/modal-store';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Sparkles, Wine, Music, Coffee, BedDouble, Utensils, Star, ChevronLeft, ChevronRight, Shield, Heart, Share2, Check, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';
import { SingleExperienceCart } from '@/components/SingleExperienceCart';
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { useUserPurchaseStatus } from '@/hooks/useUserPurchaseStatus';
import { PostPurchaseShareModal } from '@/components/experiences/PostPurchaseShareModal';
import { getFirstImageUrl } from '@/lib/utils/bookingHelpers';
import { useBookingsStore } from '@/lib/stores/bookings-store';
import { LocationMap } from '@/components/experiences/LocationMap';
import { BookingButton } from '@/components/experiences/BookingButton';

type CartItem = {
  id: string;
  product_id: string;
  quantity: number;
};
type Cart = {
  items: CartItem[];
};

type Product = {
  id: string;
  type: string;
  label: string;
  description: string;
  price: number;
};

// Static fallback for Xamanic experience
const XAMANIC_EXPERIENCE = {
  id: 'eebb9073-82f2-4bf1-8a9b-dc4e97473f38',
  title: 'Xamanic - Azores Immersion',
  media: [
    { url: 'PASTE_YOUR_CDN_IMAGE_URL_HERE', type: 'image', order_index: 0 }
  ],
  // ...other required fields as needed
};

export function XamanicExperience() {
  const { user, profile } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();
  const location = useLocation();
  const navigate = useNavigate();
  const TAB_VALUES = [
    'about',
    'itinerary',
    'accommodation',
    'location',
    'gallery',
    'faq',
  ];
  // Set initial tab from URL param if present
  const params = new URLSearchParams(window.location.search);
  const urlTab = params.get('tab');
  const initialTab = (urlTab && TAB_VALUES.includes(urlTab)) ? urlTab : 'about';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [showCart, setShowCart] = useState(false);
  const [cart, setCart] = useState<Cart>({ items: [] });
  const [cartLoading, setCartLoading] = useState(false);
  const [products] = useState<Product[]>([    
    {
      id: 'xamanic-2day',
      type: 'ticket',
      label: '2 Day Package',
      description: 'Friday & Saturday only (July 25-26, 2025)',
      price: 450,
    },
  ]);
  const experience = XAMANIC_EXPERIENCE;
  const firstImageUrl = getFirstImageUrl(experience);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    setMapMounted(true);
  }, []);

  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    // Base location for Basilicata
    const location = { lat: 40.2141054, lng: 16.6796558 };
    
    const map = new google.maps.Map(mapElement, {
      center: location,
      zoom: 10,
      mapTypeId: "satellite",
      disableDefaultUI: true, 
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
    });

    // Add markers for each hotel with correct coordinates
    new google.maps.Marker({
      position: new google.maps.LatLng(40.2141054, 16.6796558),
      map,
      title: "Marinagri Hotel & Spa"
    });

    new google.maps.Marker({
      position: new google.maps.LatLng(40.2129679, 16.678339),
      map,
      title: "Hotel Heraclea"
    });

    new google.maps.Marker({
      position: new google.maps.LatLng(40.250969, 16.6988504),
      map,
      title: "San Vincenzo Resort"
    });

    new google.maps.Marker({
      position: new google.maps.LatLng(40.1562, 16.6562),
      map,
      title: "Hotel Danaide"
    });

    new google.maps.Marker({
      position: new google.maps.LatLng(40.1981, 16.8031),
      map,
      title: "Portogreco Hotel"
    });

    new google.maps.Marker({
      position: new google.maps.LatLng(40.3394, 16.7862),
      map,
      title: "Torre del Faro"
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 2.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 2500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedMetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedMetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = () => {
    if (!user) {
      openAuthModal('sign-up');
      return;
    }
    
    // Show the cart instead of redirecting to external site
    setShowCart(true);
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Hotel data for accommodation map
  const hotels = [
    {
      name: "Marinagri Hotel & Spa",
      mapsQuery: "Marinagri Hotel Spa Policoro",
      mapsUrl: "https://maps.google.com/?q=Marinagri+Hotel+Spa+Policoro"
    },
    {
      name: "Hotel Heraclea",
      mapsQuery: "Hotel Heraclea Policoro",
      mapsUrl: "https://maps.google.com/?q=Hotel+Heraclea+Policoro"
    },
    {
      name: "San Vincenzo Resort",
      mapsQuery: "San Vincenzo Resort Policoro",
      mapsUrl: "https://maps.google.com/?q=San+Vincenzo+Resort+Policoro"
    },
    {
      name: "Hotel Danaide",
      mapsQuery: "Hotel Danaide Scanzano Jonico",
      mapsUrl: "https://maps.google.com/?q=Hotel+Danaide+Scanzano+Jonico"
    },
    {
      name: "Portogreco Hotel",
      mapsQuery: "Portogreco Hotel Scanzano Jonico",
      mapsUrl: "https://maps.google.com/?q=Portogreco+Hotel+Scanzano+Jonico"
    },
    {
      name: "Torre del Faro",
      mapsQuery: "Torre del Faro Scanzano Jonico",
      mapsUrl: "https://maps.google.com/?q=Torre+del+Faro+Scanzano+Jonico"
    }
  ];

  // Accommodation Map logic
  const accommodationMapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeTab !== 'accommodation') return;

    function tryInitMap(attempt = 0) {
      if (!accommodationMapRef.current || !window.google?.maps) {
        if (attempt < 5) {
          console.log('Map not ready, retrying...', { attempt, ref: !!accommodationMapRef.current, google: !!window.google?.maps });
          setTimeout(() => tryInitMap(attempt + 1), 300);
        } else {
          console.error('Map container or Google Maps API not ready after retries');
        }
        return;
      }

      // Clear previous map instance
      accommodationMapRef.current.innerHTML = '';

      // Initialize the map
      const map = new window.google.maps.Map(accommodationMapRef.current, {
        center: { lat: 40.2107, lng: 16.6687 },
        zoom: 11,
        mapTypeId: "roadmap",
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_BOTTOM
        },
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
          { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] }
        ]
      });

      const service = new window.google.maps.places.PlacesService(map);
      hotels.forEach(hotel => {
        service.textSearch({ query: hotel.mapsQuery }, (results: google.maps.places.PlaceResult[] | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
            const place = results[0];
            const marker = new window.google.maps.Marker({
              map,
              position: place.geometry?.location,
              title: hotel.name,
              animation: window.google.maps.Animation.DROP
            });
            const contentString = `
              <div style="padding: 12px; max-width: 260px; background: #f8f8f8; border-radius: 10px; box-shadow: 0 2px 12px rgba(0,0,0,0.15); border: 1px solid #bbb; color: #222; font-family: Arial, sans-serif;">
                <h4 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 700; color: #18181b;">${hotel.name}</h4>
                <p style="margin: 0 0 8px 0; font-size: 13px; color: #333; font-weight: 500;">${place.formatted_address || ''}</p>
                <a href="${hotel.mapsUrl}" target="_blank" style="color: #2563eb; text-decoration: underline; font-size: 13px; font-weight: 600;">Open in Google Maps →</a>
              </div>
            `;
            const infoWindow = new window.google.maps.InfoWindow({
              content: contentString,
              maxWidth: 260
            });
            marker.addListener("click", () => infoWindow.open(map, marker));
          }
        });
      });
      console.log('Map initialized successfully');
    }

    tryInitMap();
  }, [activeTab, hotels]);

  // Simulate cart API handlers (replace with real API calls)
  const handleAddToCart = async ({ productId, quantity }: { productId: string; quantity: number }) => {
    setCartLoading(true);
    setCart(prevCart => ({
      items: [
        ...(prevCart?.items || []),
        { id: productId, product_id: productId, quantity },
      ],
    }));
    setCartLoading(false);
    setShowCart(true);
  };
  const handleUpdateCartItem = () => {};
  const handleRemoveCartItem = (itemId: string) => {
    setCart(prevCart => ({
      items: (prevCart?.items || []).filter(item => item.id !== itemId),
    }));
  };
  const STRIPE_PRICE_IDS = {
    'xamanic-3day': 'price_1RPkYYI8q53ewpK1bEp10IT7', // Xamanic 3 Day Package
    'xamanic-2day': 'price_1RPkXMI8q53ewpK1ZX9iQLec', // Xamanic 2 Day Package
  };
  const handleCheckout = async () => {
    if (!user) {
      openAuthModal('sign-up');
      return;
    }
    if (!cart.items.length) {
      alert('Your cart is empty.');
      return;
    }
    // Only allow one product in cart for Xamanic
    const item = cart.items[0];
    const product = products.find(p => p.id === item.product_id);
    if (!product) {
      alert('Invalid product in cart.');
      return;
    }
    // Type guard for product_id
    if (item.product_id !== 'xamanic-3day' && item.product_id !== 'xamanic-2day') {
      alert('Invalid product in cart.');
      return;
    }
    const priceId = STRIPE_PRICE_IDS[item.product_id];
    if (!priceId) {
      alert('No Stripe price ID for this product.');
      return;
    }
    const totalAmount = product.price * item.quantity;
    try {
      await createBookingAndRedirectToStripe(
        'eebb9073-82f2-4bf1-8a9b-dc4e97473f38',
        priceId,
        item.quantity,
        totalAmount,
        '/experiences/xamanic'
      );
    } catch (error) {
      alert('There was an error starting checkout. Please try again.');
    }
  };
  const handleRequireAuth = () => openAuthModal('sign-up');

  // Only update the URL after the first render
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url);
  }, [activeTab]);

  // Scroll to content section whenever accommodation tab is active and content is rendered (robust polling for hydration)
  useEffect(() => {
    if (activeTab === 'accommodation') {
      let attempts = 0;
      const maxAttempts = 10;
      const scrollToSection = () => {
        const contentSection = document.getElementById('content-section');
        if (contentSection) {
          contentSection.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(scrollToSection, 100);
        }
      };
      scrollToSection();
    }
  }, [activeTab]);

  // Sync tab state with URL on browser navigation (popstate)
  useEffect(() => {
    const syncTabFromURL = () => {
      const params = new URLSearchParams(window.location.search);
      const tabFromURL = params.get('tab');
      if (tabFromURL && TAB_VALUES.includes(tabFromURL)) {
        setActiveTab(tabFromURL);
      }
    };
    window.addEventListener('popstate', syncTabFromURL);
    return () => window.removeEventListener('popstate', syncTabFromURL);
  }, []);

  useEffect(() => {
    console.log('activeTab changed:', activeTab);
  }, [activeTab]);

  // On mount, force tab state to match URL if a valid tab param is present (for robust deep linking)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get('tab');
    if (urlTab && TAB_VALUES.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // Only run on mount
     
  }, []);

  const [showShareModal, setShowShareModal] = useState(false);

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Xamanic Journey - Basilicata | rendezvous"
        description="Experience a transformative spiritual journey in the ancient lands of Basilicata, Italy. July 24-27, 2025."
        image="https://rendezvouswebsite.b-cdn.net/Xamanic/NEW%20Xamanic%20Save%20The%20Date%20HERO2.png"
        url="https://letsrendezvous.co/experiences/xamanic"
      />

      {/* Hero Section with Video */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <picture>
              <source 
                media="(max-width: 768px)"
                srcSet="https://rendezvouswebsite.b-cdn.net/Xamanic/NEW_Xamanic_Save_The_Date_INSTA%5B1%5D.png"
              />
              <source 
                media="(min-width: 769px)"
                srcSet="https://rendezvouswebsite.b-cdn.net/Xamanic/NEW%20Xamanic%20Save%20The%20Date%20HERO2.png"
              />
              <img
                src="https://rendezvouswebsite.b-cdn.net/Xamanic/Xamanic%20Save%20The%20Date%20HERO2%5B1%5D.png"
                alt="Xamanic Journey"
                className="w-full h-full object-cover object-center"
              />
            </picture>
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Xamanic/Mezcal%20Xam%C3%A0n%20-%20Part%201.mov"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Xamanic/Xamanic%20Flyer%20Widescreen.png"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content - Positioned Lower */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-center px-4 text-center">
          

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Content Section with Tabs */}
      <div id="content-section" className="container mx-auto px-4 pt-8 pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Purchase Button at Top of Content */}
          <div className="flex justify-center mb-8 -pt-10">
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="text-lg px-10 py-6 shadow-lg hover:scale-105 transition-transform"
            >
              Purchase Ticket
            </Button>
          </div>
          
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                <div className="overflow-x-auto -mx-4 px-4">
                  <TabsList className="w-full min-w-max">
                    <TabsTrigger value="about">About</TabsTrigger>
                    <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
                    <TabsTrigger value="accommodation">Accommodation</TabsTrigger>
                    <TabsTrigger value="location">Location</TabsTrigger>
                    <TabsTrigger value="gallery">Gallery</TabsTrigger>
                    <TabsTrigger value="faq">FAQ</TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About the Experience</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                    Step into a dream of music, mezcal, and the wild beauty of Basilicata. Over the course of two unforgettable days we gather to dance into the night, share long tables under open skies, and celebrate the spirit of Mezcal Xamán.
                    </p>
                    <p>
                    Our Xamanic Journey is an invitation to connect, with each other, and with the true self that lies within. A meeting of friends and kindred spirits, drawn together by music, astonishing places, and the beauty of now.
                    </p>
                    <p>
                    Whether you're seeking radical self expression, cultural enrichment, or simply a profound experience in one of Italy's most beautiful hidden regions, this journey offers a carefully curated space for exploration and celebration.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">What's Included</h3>
                      <ul className="list-disc list-inside space-y-2">
                        <li>Farm-to-table dinners (including local wine)</li>
                        <li>Access to both parties (Friday and Saturday)</li>
                        <li>Mezcal open bar (including delicious cocktails and beer)</li>
                        <li>Transportation between selected hotels and event venues</li>
                      </ul>
                    </div>

                    <div className="space-y-4 text-sand-300">
                      <h3 className="text-xl font-display mb-3">Our Philosophy</h3>
                      <p>
                        Xamán is a celebration of place, presence, and people.
</p>
                    <p>
We believe in the art of gathering slowly and intentionally in wild places that ask us to listen. In music that opens something ancient. In mezcal that holds the memory of the earth, and in the simple beauty of sharing a table under the stars.
</p>
                    <p>
Rooted in sustainability and guided by energy, our experiences invite you to slow down, tune in, and feel more deeply.
</p>
                    <p>
This is not just a party. It is a space to feel, to dance, to reconnect - to the ground beneath us and the people beside us.
</p>
                    <p>
During our main night on Saturday, we encourage everyone to dance barefoot and connect with Mother Earth. To facilitate that, reduce plastic consumption, and respect our surroundings, the event will be glass-free and we invite participants to bring their own drinking vessels. A sustainable option will also be available to purchase on our website.
                      </p>
                    </div>
                    <div className="space-y-4 text-sand-300">
                      <h3 className="text-xl font-display mb-3">Getting Here</h3>
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/xamanic%20full%20map%20(white%20text).png"
                        alt="Xamanic Journey Location Map" 
                        className="w-full rounded-lg"
                      />
                    </div>
                  </div>
                  
                  
                  {/* Media Gallery - Added based on ArtHouse layout */}
                  <div className="mt-8">
                    <h3 className="text-xl font-display mb-6">Gallery</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/_DSC0023.ARW.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />                      
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0110.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0117.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0077.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0264.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0008.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0010.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0012.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />                      
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0380%201.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0435%201.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/_DSC9985.ARW.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0034.jpg" 
                        alt="Xamanic Journey" 
                        className="w-full h-64 object-cover rounded-lg"
                      />                      
                      {/* New images from Media Gallery 2 */}
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0005.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0007.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0013.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                      <img src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery%202/IMG-20250517-WA0016.jpg" alt="Xamanic Journey" className="w-full h-64 object-cover rounded-lg" />
                    </div>
                    <div></div>
                  </div>
                </TabsContent>
                
                <TabsContent value="itinerary" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Journey Itinerary</h2>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Day 1: Castello Fantastico</h3>
                      <p className="text-sand-300 mb-4">@Castello di San Basilio - Friday 25th of July</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>7:00 PM - Aperitivo Welcome Reception</li>
                        <li>9:00 PM - Seated Dinner Experience</li>
                        <li>10:30 PM - 3:00 AM - Boogie Celebration</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Day 2: La Danza del Sole e della Luna</h3>
                      <p className="text-sand-300 mb-4">@Teatro dei Calanchi - Saturday 26th of July</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Sunset to Sunrise Breathtaking Experience</li>
                        <li>9:00 PM - Seated Dinner Under the Stars</li>
                        <li>11:00 PM - Sunrise - Calanchi Rave celebration</li>
                      </ul>
                    </div>
                  </div>
                    
                </TabsContent>

                <TabsContent value="accommodation" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Recommended Hotel Areas</h2>
                  <div className="w-full h-96 rounded-lg overflow-hidden mb-8">
                    <div ref={accommodationMapRef} className="w-full h-full" />
                  </div>
                  <p className="text-sand-300 mb-8">
                    We recommend a selection of hotels in the region, chosen for their comfort & convenience. Thoughtfully selected and close to our event venues, these accommodations offer a restful pause between gatherings.
                  </p>
                  <p className="text-sand-300 mb-8">
                    Shuttle service is provided between select hotels and Xamanic locations throughout the weekend.
                  </p>
                  <p className="text-sand-300 mb-8">
                  If you choose to stay elsewhere in the area, please let us know in advance, we'll do our best to assist with transport coordination where possible.
                  </p>
                  
                  <div className="space-y-8">
                    {/* Hotel Area 1: Marinagri */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                        <div className="md:col-span-1">
                          <img 
                            src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/resort_5_stelle_in_basilicata_2_box.jpg" 
                            alt="Marinagri Area" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="md:col-span-2 p-6">
                          <h3 className="text-2xl font-display mb-2">Hotel Area 1: Marinagri</h3>
                          <p className="text-sand-300 mb-4">
                            An exclusive marina complex with luxury accommodation in a beautiful setting.
                          </p>
                          <p className="text-sand-300 mb-4">
                            <strong>Recommended Hotels:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-2 text-sand-300 mb-4">
                            <li>Marinagri Hotel & Spa</li>
                          </ul>
                          <p className="text-sand-300 text-sm">
                            <a href="https://maps.google.com/?q=Marinagri+Hotel+Spa+Policoro" className="text-sand-100 hover:text-sand-200 underline" target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Hotel Area 2: Lido di Policoro */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                        <div className="md:col-span-1">
                          <img 
                            src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/piazzale-piscina.jpg" 
                            alt="Lido di Policoro Area" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="md:col-span-2 p-6">
                          <h3 className="text-2xl font-display mb-2">Hotel Area 2: Lido di Policoro</h3>
                          <p className="text-sand-300 mb-4">
                            A vibrant area with modern amenities and excellent service.
                          </p>
                          <p className="text-sand-300 mb-4">
                            <strong>Recommended Hotels:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-2 text-sand-300 mb-4">
                            <li>Hotel Heraclea</li>
                            <li>San Vincenzo Resort</li>
                          </ul>
                          <p className="text-sand-300 text-sm">
                            <a href="https://maps.google.com/?q=Hotel+Heraclea+Policoro" className="text-sand-100 hover:text-sand-200 underline mr-4" target="_blank" rel="noopener noreferrer">Hotel Heraclea</a>
                            <a href="https://maps.google.com/?q=San+Vincenzo+Resort+Policoro" className="text-sand-100 hover:text-sand-200 underline" target="_blank" rel="noopener noreferrer">San Vincenzo Resort</a>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Hotel Area 3: Scanzano */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                        <div className="md:col-span-1">
                          <img 
                            src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/7941_12976_275661.jpg" 
                            alt="Scanzano Area" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="md:col-span-2 p-6">
                          <h3 className="text-2xl font-display mb-2">Hotel Area 3: Scanzano</h3>
                          <p className="text-sand-300 mb-4">
                            Beautiful resorts along the coastline with easy access to beach and amenities.
                          </p>
                          <p className="text-sand-300 mb-4">
                            <strong>Recommended Hotels:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-2 text-sand-300 mb-4">
                            <li>Hotel Danaide</li>
                            <li>Portogreco Hotel</li>
                            <li>Torre del Faro</li>
                          </ul>
                          <p className="text-sand-300 text-sm">
                            <a href="https://maps.google.com/?q=Hotel+Danaide+Scanzano+Jonico" className="text-sand-100 hover:text-sand-200 underline mr-4" target="_blank" rel="noopener noreferrer">Hotel Danaide</a>
                            <a href="https://maps.google.com/?q=Portogreco+Hotel+Scanzano+Jonico" className="text-sand-100 hover:text-sand-200 underline mr-4" target="_blank" rel="noopener noreferrer">Portogreco Hotel</a>
                            <a href="https://maps.google.com/?q=Torre+del+Faro+Scanzano+Jonico" className="text-sand-100 hover:text-sand-200 underline" target="_blank" rel="noopener noreferrer">Torre del Faro</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="location" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Location</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <div>
                      <h3 className="text-xl font-display mb-4">Pisticci, Basilicata, Italy</h3>
                      <p className="text-sand-300 mb-4">
                        Our Xamanic Journey takes place in the stunning land of Basilicata, a wild and underexplored region of southern Italy, centered around the hilltop town of Pisticci, known for its whitewashed houses and sweeping views of the Calanchi badlands.
                      </p>
                      <p className="text-sand-300">
                        Events are hosted across three remarkable venues:
                      </p>
                      <ul className="list-disc list-inside mt-4 space-y-3 text-sand-300">
                        <li>Il Castello di San Basilio, a 7th-century castle turned cultural residence where history and art quietly converge.</li>
                        <li>Il Teatro dei Calanchi, a surreal natural amphitheater carved from clay ridges where sound, light, and earth meet in open air.</li>                        
                      </ul>
                      <p className="mt-4 text-sand-300">
                        Together, these spaces form a backdrop that's both grounded and dreamlike, perfect for gathering, dancing, and reconnecting in true Xamanic style.
                      </p>
                    </div>
                    <div className="flex flex-col space-y-4">
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/Pisticci-shutterstock_1033599547-1024x768-f16d452b805943867f1f0f6021cb7f4f.jpg" 
                        alt="Pisticci, Basilicata" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/-k3a2775-1_2_300053-173252200744613.jpeg" 
                        alt="Teatro dei Calanchi" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/TdC-workshop01.jpg" 
                        alt="Xamanic Experience" 
                        className="w-full h-48 object-cover rounded-lg"
                      />                      
                    </div>
                  </div>

                  <div className="mt-8 bg-earth-800/50 p-6 rounded-lg">
                    <h3 className="text-xl font-display mb-4">Travel Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-sand-100 mb-2">Nearest Airports</h4>
                        <ul className="space-y-2 text-sand-300">
                          <li className="flex items-center gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Bari International Airport (1.5hr drive)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Brindisi Airport (1.5hr drive)</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-sand-100 mb-2">Local Transportation</h4>
                        <p className="text-sand-300">
                          For airport transfers we recommend arranging transportation in advance. Car rentals available at airports. Private and shared shuttle services can also be arranged through our trusted transport partner.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="gallery" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Gallery</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/_DSC0023.ARW.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/_DSC0275.ARW.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />                    
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/_DSC9985.ARW.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0034.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0077.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0110.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0117.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0264.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0380%201.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC0435%201.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />                    
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC9916.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Media%20Gallery/EXP-_DSC9925.jpg" 
                      alt="Xamanic Journey" 
                      className="w-full h-64 object-cover rounded-lg"
                    />                    
                  </div>
                </TabsContent>
                
                <TabsContent value="faq" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the Xamanic Journey experience?</h3>
                      <p className="text-sand-300">
                        The Xamanic Journey by Mezcal Xamán is our signature event combining music, people, and mezcal in a timeless celebration. It includes curated events at magical locations throughout Basilicata, bringing together friendship, cultural appreciation, and spiritual connection.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What should I pack?</h3>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Comfortable, light clothing appropriate for Mediterranean summer weather, with layers for cooler evenings</li>
                        <li>We recommend outfits for each of the themed nights, something you feel amazing in!</li>
                        <li>Comfortable shoes for dancing all night long</li>
                        <li>A reusable drinking cup (we encourage sustainability, and leaving no trace)</li>
                        <li>Sunscreen, sunglasses</li>
                        <li>An open heart and sense of adventure.</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">How do I get around during the event?</h3>
                      <p className="text-sand-300">
                        Shuttle services are included between selected hotels/pickup areas and event venues. Details about pickup times will be provided before each event. For explorations outside the event schedule, we recommend renting a car or arranging transportation through your hotel or our trusted transport partner. All venues have parking spaces for those of you having a designated driver.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the cancellation policy?</h3>
                      <p className="text-sand-300">
                        All sales are final. However, tickets can be transferred to another person if you're unable to attend. Please contact us for assistance with transferring your ticket.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What's included in my ticket?</h3>
                      <p className="text-sand-300">
                        Your ticket includes access to all Xamanic Journey events, two gourmet dinners, open bar, and shuttle transportation between participating hotels and event venues. Accommodation must be booked separately at one of our partner hotels.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What will be the Dress code for the themed nights?</h3>
                      <div className="text-sand-300">
                        <p className="font-medium text-sand-100 mt-4 mb-2">FRIDAY NIGHT</p>
                        
                        <p>Castello Fantastico: Bold prints, retro glamour</p>
                        
                        <p>Flowing caftans, printed retro silks, rich jewel tones, wide-leg trousers, feathers, and playful elegance.</p>
                        
                        <p>Mood: Retro surrealism meets aperitivo excess. Express yourself — and dress like you're the main character in someone's strange, fabulous memory.</p>
                        
                        <p className="font-medium text-sand-100 mt-4 mb-2">SATURDAY NIGHT</p>
                        
                        <p>La Danza del Sole e della Luna: Natural fabrics with celestial energy.</p>
                        
                        <p>Metallic fabrics in bone, silver, and gold — flowing and elegant.</p>
                        
                        <p>Suggested materials: Silk, linen, brocade, gauze, crinkled cotton, raw-edged layers</p>
                        
                        <p>Colors: Bone, ivory, bronze, aged gold, antique silver, moonlit metallics</p>
                        
                        <p>Mood: Dress like the moon kissed your shoulders and the sun stitched your hemline</p>
                                                
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display">2 Day Package</h3>
                    <div className="text-2xl font-display">€450</div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>2 nights, 2 dinners</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-5 w-5 text-sand-400" />
                      <span>July 25-27th, 2025</span>
                    </div>
                    <Button
                      className="w-full mt-4"
                      onClick={() => setShowCart(true)}
                    >
                      Purchase 2 Day Package
                    </Button>
                  </div>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Host</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://rendezvouswebsite.b-cdn.net/Xamanic/Logo/Soft%20Xaman%20Logo%20Insta.png" 
                      alt="Mezcal Xamán"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=MX&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Mezcal Xamán</p>
                      <p className="text-sand-400 text-sm">The One Who Knows</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm">
                    Xamán is a celebration of place, presence, and people. We gather slowly and intentionally in wild spaces that ask us to listen - to music, to mezcal, to each other. Rooted in sustainability and guided by feeling, our experiences invite you to tune in, move freely, and reconnect - with the land, and the people beside you.
                  </p>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Wine className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Mezcal and cocktails flowing throughout the weekend</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Utensils className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Long-table dinners in extraordinary settings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Music className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Access to Xamanic Nights gatherings and performances</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Shuttle service between select hotels and event locations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
            <h2 className="text-3xl font-display mb-6">Begin Your Journey</h2>
            <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
              Spaces are limited to ensure an intimate and transformative experience for all participants. Secure your place now on this life-changing Xamanic Journey in the magical landscapes of Basilicata.
            </p>
            {user && (
              <Button 
                size="lg"
                onClick={() => setShowShareModal(true)}
                className="text-lg px-8 py-4"
              >
                <Users className="h-5 w-5 mr-2" />
                Bring Your Friends
              </Button>
            )}
          </div>
        </div>
      </div>



      {/* Cart Modal/Bottom Sheet */}
      {showCart && (
        <SingleExperienceCart
          experience={{ title: 'Xamanic Journey' }}
          products={products}
          cart={cart}
          onAddToCart={handleAddToCart}
          onUpdateCartItem={handleUpdateCartItem}
          onRemoveCartItem={handleRemoveCartItem}
          onCheckout={handleCheckout}
          loading={cartLoading}
          onRequireAuth={handleRequireAuth}
        />
      )}

      <PostPurchaseShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        experienceTitle={experience.title}
        experienceUrl={window.location.href}
        experienceImage={firstImageUrl}
        onBuyMoreTickets={() => {
          setShowShareModal(false);
          setShowCart(true);
        }}
      />
    </div>
  );
}